/** Client-side branded payment-request email (mirrors Edge template). */

export type PaymentRequestVars = {
  clientName: string;
  venueName: string;
  reference: string;
  performanceDate: string;
  performanceTime: string;
  address: string;
  feeDisplay: string;
  dueDate: string;
  payUrl: string;
  description: string;
  signOff: string;
};

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(s: string) {
  return escapeHtml(s).replace(/'/g, "&#39;");
}

export function buildPaymentRequestEmail(v: PaymentRequestVars): {
  subject: string;
  html: string;
  text: string;
} {
  const greeting = v.clientName ? `Dear ${v.clientName},` : "Hello,";
  const subject = `Payment request · ${v.reference} · Olivia Flemming`;

  const text = [
    greeting,
    "",
    "Thank you for booking Olivia Flemming.",
    "",
    `Reference: ${v.reference}`,
    `Venue / event: ${v.venueName}`,
    `Date: ${v.performanceDate}`,
    `Time: ${v.performanceTime}`,
    `Location: ${v.address || "—"}`,
    `Fee: ${v.feeDisplay}`,
    `Payment due: ${v.dueDate || "—"}`,
    v.description ? `Details: ${v.description}` : "",
    "",
    "Please pay securely using this link:",
    v.payUrl,
    "",
    "If you have any questions, simply reply to this email.",
    "",
    "With warm regards,",
    v.signOff,
    "oliviaflemming.co.uk",
  ]
    .filter((line) => line !== "")
    .join("\n");

  const html = `<!DOCTYPE html>
<html lang="en">
<head><meta charset="utf-8" /><meta name="viewport" content="width=device-width" /></head>
<body style="margin:0;padding:0;background:#0D0D0D;color:#F5F0EB;font-family:Georgia,'Times New Roman',serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#0D0D0D;padding:32px 16px;">
    <tr><td align="center">
      <table role="presentation" width="560" cellspacing="0" cellpadding="0" style="max-width:560px;background:#1A1A1A;border:1px solid #2A2A2A;border-radius:16px;overflow:hidden;">
        <tr><td style="padding:28px 28px 8px;text-align:center;letter-spacing:0.28em;font-size:11px;color:#C4956A;text-transform:uppercase;font-family:Helvetica,Arial,sans-serif;">Olivia Flemming</td></tr>
        <tr><td style="padding:8px 28px 4px;text-align:center;font-size:26px;color:#FFFFFF;">Payment request</td></tr>
        <tr><td style="padding:0 28px 20px;text-align:center;font-size:13px;color:#A0A0A0;font-family:Helvetica,Arial,sans-serif;">${escapeHtml(v.reference)}</td></tr>
        <tr><td style="padding:0 28px 24px;font-size:15px;line-height:1.6;color:#F5F0EB;font-family:Helvetica,Arial,sans-serif;">
          <p style="margin:0 0 16px;">${escapeHtml(greeting)}</p>
          <p style="margin:0 0 16px;">Thank you for booking Olivia Flemming. Please find the performance details and secure payment link below.</p>
          <table role="presentation" width="100%" style="background:#0D0D0D;border-radius:12px;margin:0 0 20px;">
            <tr><td style="padding:16px;font-size:13px;line-height:1.7;color:#D0D0D0;">
              <strong style="color:#C4956A;">Venue</strong><br/>${escapeHtml(v.venueName)}<br/><br/>
              <strong style="color:#C4956A;">Date</strong><br/>${escapeHtml(v.performanceDate)}<br/><br/>
              <strong style="color:#C4956A;">Time</strong><br/>${escapeHtml(v.performanceTime)}<br/><br/>
              <strong style="color:#C4956A;">Location</strong><br/>${escapeHtml(v.address || "—")}<br/><br/>
              <strong style="color:#C4956A;">Fee</strong><br/>${escapeHtml(v.feeDisplay)}<br/><br/>
              <strong style="color:#C4956A;">Due</strong><br/>${escapeHtml(v.dueDate || "—")}
              ${v.description ? `<br/><br/><strong style="color:#C4956A;">Details</strong><br/>${escapeHtml(v.description)}` : ""}
            </td></tr>
          </table>
          <p style="text-align:center;margin:0 0 24px;">
            <a href="${escapeAttr(v.payUrl)}" style="display:inline-block;background:#C4956A;color:#0D0D0D;text-decoration:none;font-family:Helvetica,Arial,sans-serif;font-size:14px;font-weight:600;padding:14px 28px;border-radius:999px;">Pay securely</a>
          </p>
          <p style="margin:0 0 8px;font-size:12px;color:#808080;word-break:break-all;">Or copy this link:<br/>${escapeHtml(v.payUrl)}</p>
          <p style="margin:24px 0 0;">With warm regards,<br/><strong>${escapeHtml(v.signOff)}</strong></p>
        </td></tr>
        <tr><td style="padding:16px 28px 28px;text-align:center;font-size:11px;color:#666;font-family:Helvetica,Arial,sans-serif;">oliviaflemming.co.uk</td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

  return { subject, html, text };
}
